import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import IncidentClusterMap from './IncidentClusterMap';
import IncidentHeatmap from './IncidentHeatmap';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
);

// Helper functions - Always use Melbourne timezone
function getMonthYear(dateString) {
  const d = new Date(dateString);
  // Convert to Melbourne timezone for consistent month/year calculation
  const melbourneDate = new Date(d.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  return `${melbourneDate.getFullYear()}-${String(melbourneDate.getMonth() + 1).padStart(2, '0')}`;
}

function getHourOfDay(dateString) {
  // Parse the date string and ensure it's treated as Melbourne time
  let d;
  if (dateString.endsWith('Z')) {
    // UTC date - convert to Melbourne time
    d = new Date(dateString);
  } else {
    // Local date - assume it's already in Melbourne time
    d = new Date(dateString);
  }

  // Always convert to Melbourne timezone for consistent hour calculation
  const melbourneDate = new Date(d.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  const hour = melbourneDate.getHours();
  console.log(
    '🔍 DEBUG getHourOfDay:',
    dateString,
    '-> original:',
    d.getHours(),
    'melbourne:',
    hour,
  );
  return hour;
}

function getDayOfWeek(dateString) {
  const d = new Date(dateString);
  // Convert to Melbourne timezone for consistent day calculation
  const melbourneDate = new Date(d.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
  return melbourneDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
}

function getVehicleType(incidentType) {
  if (!incidentType) return 'Other';

  if (incidentType.includes('car')) return 'Cars';
  if (incidentType.includes('motorcycle')) return 'Motorcycles';
  if (incidentType.includes('bicycle')) return 'Bicycles';
  if (incidentType.includes('scooter')) return 'Scooters';
  if (incidentType.includes('truck')) return 'Trucks';
  if (incidentType.includes('pedestrian')) return 'Pedestrians';
  if (incidentType.includes('animal')) return 'Animals';

  return 'Other';
}

function getInjurySeverity(report) {
  if (!report.injuries || report.injuries === 'no') return 'No Injuries';
  if (report.injuries === 'minor') return 'Minor Injuries';
  if (report.injuries === 'major') return 'Major Injuries';
  if (report.injuries === 'fatal') return 'Fatalities';
  return 'Unknown';
}

export default function GraphsAndStats({ reports, user }) {
  // State for filtering and map view
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [includePending, setIncludePending] = useState(false);
  const [mapView, setMapView] = useState('heatmap');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs for chart elements to capture for PDF
  const lineChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const timeOfDayChartRef = useRef(null);
  const peakHoursChartRef = useRef(null);
  const mapRef = useRef(null);

  // Filter reports based on current filters
  const filtered = useMemo(() => {
    try {
      if (!reports || !Array.isArray(reports)) {
        return [];
      }

      return reports.filter(r => {
        if (!r || typeof r !== 'object') return false;
        if (!includePending && !r.isApproved) return false;
        if (typeFilter !== 'all' && r.incidentType !== typeFilter) return false;
        if (dateRange.from && r.estimatedIncidentTime) {
          const reportDate = new Date(r.estimatedIncidentTime);
          const melbourneReportDate = new Date(
            reportDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
          );
          const fromDate = new Date(dateRange.from);
          const melbourneFromDate = new Date(
            fromDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
          );
          if (melbourneReportDate < melbourneFromDate) return false;
        }
        if (dateRange.to && r.estimatedIncidentTime) {
          const reportDate = new Date(r.estimatedIncidentTime);
          const melbourneReportDate = new Date(
            reportDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
          );
          const toDate = new Date(dateRange.to);
          const melbourneToDate = new Date(
            toDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
          );
          if (melbourneReportDate > melbourneToDate) return false;
        }
        return true;
      });
    } catch (err) {
      console.error('Error filtering reports:', err);
      return [];
    }
  }, [reports, includePending, typeFilter, dateRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    try {
      if (!filtered || !Array.isArray(filtered)) {
        return {
          total: 0,
          thisMonth: 0,
          totalInjuries: 0,
          injuryRate: 0,
          peakHour: '—',
          peakHourCount: 0,
          mostCommonType: '—',
          typeCounts: {},
        };
      }

      // Get current month in Melbourne timezone for consistent comparison
      const now = new Date();
      const melbourneNow = new Date(
        now.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
      );
      const currentMonth = `${melbourneNow.getFullYear()}-${String(melbourneNow.getMonth() + 1).padStart(2, '0')}`;

      // Basic counts
      const total = filtered.length;
      const thisMonth = filtered.filter(r => {
        if (!r || !r.estimatedIncidentTime) return false;
        return getMonthYear(r.estimatedIncidentTime) === currentMonth;
      }).length;

      // Type counts
      const typeCounts = {};
      filtered.forEach(r => {
        if (r && r.incidentType) {
          typeCounts[r.incidentType] = (typeCounts[r.incidentType] || 0) + 1;
        }
      });

      // Most common type
      const mostCommonType = Object.keys(typeCounts).reduce(
        (a, b) => (typeCounts[a] > typeCounts[b] ? a : b),
        '—',
      );

      // Time of day analysis with better error handling
      const timeOfDayCounts = {};
      console.log('🔍 DEBUG: Processing', filtered.length, 'reports for peak hour calculation');
      let excludedCount = 0;
      filtered.forEach(r => {
        if (r && r.estimatedIncidentTime && !r.timeUnknown && !r.isDateRange) {
          try {
            const hour = getHourOfDay(r.estimatedIncidentTime);
            console.log(
              '🔍 DEBUG: Report',
              r.id,
              'date:',
              r.estimatedIncidentTime,
              'hour:',
              hour,
              'timeUnknown:',
              r.timeUnknown,
              'isDateRange:',
              r.isDateRange,
            );
            if (hour >= 0 && hour <= 23) {
              timeOfDayCounts[hour] = (timeOfDayCounts[hour] || 0) + 1;
            }
          } catch (error) {
            console.warn('Invalid date format for report:', r.id, r.estimatedIncidentTime);
          }
        } else {
          excludedCount++;
          console.log(
            '🔍 DEBUG: Report',
            r.id,
            'EXCLUDED from peak hour - timeUnknown:',
            r.timeUnknown,
            'isDateRange:',
            r.isDateRange,
          );
        }
      });
      console.log('🔍 DEBUG: Excluded reports from chart:', excludedCount);
      console.log('🔍 DEBUG: Time of day counts:', timeOfDayCounts);
      console.log('🔍 DEBUG: Time of day counts JSON:', JSON.stringify(timeOfDayCounts, null, 2));

      // Simple timezone test
      const testDate = new Date('2025-10-15T16:00:00');
      const testMelbourne = new Date(
        testDate.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }),
      );
      console.log(
        '🔍 TIMEZONE TEST: 16:00 UTC should be',
        testMelbourne.getHours(),
        'in Melbourne (should be 2 or 3 depending on DST)',
      );

      // Peak hour with better handling
      const peakHour =
        Object.keys(timeOfDayCounts).length > 0
          ? Object.keys(timeOfDayCounts).reduce((a, b) =>
              timeOfDayCounts[a] > timeOfDayCounts[b] ? a : b,
            )
          : '—';
      const peakHourCount = timeOfDayCounts[peakHour] || 0;

      // Injury analysis
      const incidentsWithInjuries = filtered.filter(
        r => r && r.injuries && r.injuries !== 'no',
      ).length;

      const totalInjuries = filtered.reduce((sum, r) => {
        if (!r || !r.injuries || r.injuries === 'no') return sum;
        if (r.injuryCount && typeof r.injuryCount === 'number') {
          return sum + r.injuryCount;
        }
        return sum + 1; // Fallback to 1 injury per incident if no count specified
      }, 0);

      const injuryRate = total > 0 ? Math.round((incidentsWithInjuries / total) * 100) : 0;

      return {
        total,
        thisMonth,
        totalInjuries,
        injuryRate,
        peakHour: peakHour !== '—' ? `${peakHour}:00` : '—',
        peakHourCount,
        mostCommonType,
        typeCounts,
      };
    } catch (err) {
      console.error('Error calculating stats:', err);
      return {
        total: 0,
        thisMonth: 0,
        totalInjuries: 0,
        injuryRate: 0,
        peakHour: '—',
        peakHourCount: 0,
        mostCommonType: '—',
        typeCounts: {},
      };
    }
  }, [filtered]);

  // Chart options for PDF rendering
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: 'Arial, sans-serif',
          },
          color: '#374151',
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 10,
            family: 'Arial, sans-serif',
          },
          color: '#6B7280',
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 10,
            family: 'Arial, sans-serif',
          },
          color: '#6B7280',
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  // Chart data
  const lineData = useMemo(() => {
    try {
      if (!filtered || !Array.isArray(filtered)) {
        return { labels: [], datasets: [] };
      }

      const monthlyData = {};
      filtered.forEach(r => {
        if (r && r.estimatedIncidentTime) {
          const monthYear = getMonthYear(r.estimatedIncidentTime);
          monthlyData[monthYear] = (monthlyData[monthYear] || 0) + 1;
        }
      });

      const sortedMonths = Object.keys(monthlyData).sort();

      return {
        labels: sortedMonths,
        datasets: [
          {
            label: 'Incident Reports',
            data: sortedMonths.map(month => monthlyData[month]),
            borderColor: 'rgb(37, 99, 235)',
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            fill: true,
            tension: 0.4,
          },
        ],
      };
    } catch (err) {
      console.error('Error creating line chart data:', err);
      return { labels: [], datasets: [] };
    }
  }, [filtered]);

  const pieData = useMemo(() => {
    try {
      if (!filtered || !Array.isArray(filtered)) {
        return { labels: [], datasets: [] };
      }

      const typeCounts = {};
      filtered.forEach(r => {
        if (r && r.incidentType) {
          typeCounts[r.incidentType] = (typeCounts[r.incidentType] || 0) + 1;
        }
      });

      const colors = [
        '#3B82F6',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#06B6D4',
        '#84CC16',
        '#F97316',
        '#EC4899',
        '#6366F1',
      ];

      return {
        labels: Object.keys(typeCounts),
        datasets: [
          {
            data: Object.values(typeCounts),
            backgroundColor: colors.slice(0, Object.keys(typeCounts).length),
            borderWidth: 2,
            borderColor: '#ffffff',
          },
        ],
      };
    } catch (err) {
      console.error('Error creating pie chart data:', err);
      return { labels: [], datasets: [] };
    }
  }, [filtered]);

  const timeOfDayData = useMemo(() => {
    try {
      if (!filtered || !Array.isArray(filtered)) {
        return { labels: [], datasets: [] };
      }

      const timeOfDayCounts = {};
      for (let i = 0; i < 24; i++) {
        timeOfDayCounts[i] = 0;
      }

      console.log('🔍 DEBUG: Processing', filtered.length, 'reports for TIME OF DAY CHART');
      let chartExcludedCount = 0;
      filtered.forEach(r => {
        if (r && r.estimatedIncidentTime && !r.timeUnknown && !r.isDateRange) {
          const hour = getHourOfDay(r.estimatedIncidentTime);
          timeOfDayCounts[hour] = (timeOfDayCounts[hour] || 0) + 1;
        } else {
          chartExcludedCount++;
        }
      });
      console.log('🔍 DEBUG: Chart excluded reports:', chartExcludedCount);
      console.log('🔍 DEBUG: Chart time of day counts:', timeOfDayCounts);

      const labels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
      const data = Array.from({ length: 24 }, (_, i) => timeOfDayCounts[i] || 0);

      return {
        labels,
        datasets: [
          {
            label: 'Incidents',
            data,
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
            tension: 0.4,
          },
        ],
      };
    } catch (err) {
      console.error('Error creating time of day chart data:', err);
      return { labels: [], datasets: [] };
    }
  }, [filtered]);

  const peakHoursData = useMemo(() => {
    try {
      if (!filtered || !Array.isArray(filtered)) {
        return { labels: [], datasets: [] };
      }

      const dayOfWeekCounts = {};
      for (let i = 0; i < 7; i++) {
        dayOfWeekCounts[i] = 0;
      }

      filtered.forEach(r => {
        if (r && r.estimatedIncidentTime) {
          const dayOfWeek = getDayOfWeek(r.estimatedIncidentTime);
          dayOfWeekCounts[dayOfWeek] = (dayOfWeekCounts[dayOfWeek] || 0) + 1;
        }
      });

      const dayNames = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      const labels = dayNames;
      const data = Array.from({ length: 7 }, (_, i) => dayOfWeekCounts[i] || 0);

      return {
        labels,
        datasets: [
          {
            label: 'Incidents',
            data,
            backgroundColor: 'rgba(139, 92, 246, 0.8)',
            borderColor: 'rgb(139, 92, 246)',
            borderWidth: 1,
          },
        ],
      };
    } catch (err) {
      console.error('Error creating day of week chart data:', err);
      return { labels: [], datasets: [] };
    }
  }, [filtered]);

  // Debug effect to check ref attachment
  useEffect(() => {
    console.log('🔍 Chart refs debug:', {
      lineChartRef: lineChartRef.current,
      pieChartRef: pieChartRef.current,
      timeOfDayChartRef: timeOfDayChartRef.current,
      peakHoursChartRef: peakHoursChartRef.current,
      filteredLength: filtered.length,
      lineData,
      pieData,
      timeOfDayData,
      peakHoursData,
      reports: reports?.length || 0,
      isLoading,
      error,
    });
  }, [filtered, lineData, pieData, timeOfDayData, peakHoursData, reports, isLoading, error]);

  // Debug effect to check when refs get attached
  useEffect(() => {
    const checkRefs = () => {
      console.log('🔍 Ref attachment check:', {
        lineChartRef: lineChartRef.current,
        pieChartRef: pieChartRef.current,
        timeOfDayChartRef: timeOfDayChartRef.current,
        peakHoursChartRef: peakHoursChartRef.current,
      });
    };

    // Check immediately
    checkRefs();

    // Check after a short delay
    setTimeout(checkRefs, 100);
    setTimeout(checkRefs, 500);
    setTimeout(checkRefs, 1000);
  }, []);

  // Clean PDF Export function
  const exportToPDF = async () => {
    try {
      // Don't start PDF export if component is already loading
      if (isLoading) {
        console.log('🔍 Component is loading, waiting for charts to be ready...');
        return;
      }

      // Check if charts are rendered
      if (
        !lineChartRef.current ||
        !pieChartRef.current ||
        !timeOfDayChartRef.current ||
        !peakHoursChartRef.current
      ) {
        console.log('🔍 Charts not rendered yet, waiting...');
        return;
      }

      setError(null);

      console.log('📊 Starting PDF export...');

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('l', 'mm', 'a4');

      // Wait for charts to be ready and ensure they're fully rendered
      console.log('📊 Waiting for charts to render...');

      // Debug: Check refs before waiting
      console.log('🔍 Ref status check:', {
        lineChartRef: lineChartRef.current,
        pieChartRef: pieChartRef.current,
        timeOfDayChartRef: timeOfDayChartRef.current,
        peakHoursChartRef: peakHoursChartRef.current,
      });

      // Force chart re-render by triggering a resize event
      window.dispatchEvent(new Event('resize'));

      // Wait for charts to be fully rendered
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Additional wait to ensure canvas elements are ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Debug: Check refs after waiting
      console.log('🔍 Ref status after waiting:', {
        lineChartRef: lineChartRef.current,
        pieChartRef: pieChartRef.current,
        timeOfDayChartRef: timeOfDayChartRef.current,
        peakHoursChartRef: peakHoursChartRef.current,
      });

      // Additional check: verify canvas elements exist
      const checkCanvasElements = () => {
        const canvases = {
          lineChart: lineChartRef.current?.querySelector('canvas'),
          pieChart: pieChartRef.current?.querySelector('canvas'),
          timeOfDayChart: timeOfDayChartRef.current?.querySelector('canvas'),
          peakHoursChart: peakHoursChartRef.current?.querySelector('canvas'),
        };
        console.log('🔍 Canvas elements check:', canvases);
        return Object.values(canvases).every(canvas => canvas !== null);
      };

      // Wait for canvas elements to be ready
      let attempts = 0;
      while (!checkCanvasElements() && attempts < 10) {
        console.log(`🔍 Waiting for canvas elements... attempt ${attempts + 1}`);
        await new Promise(resolve => setTimeout(resolve, 500));
        attempts++;
      }

      // Page dimensions (landscape)
      const pageWidth = 297;
      const pageHeight = 210;
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;
      let yPosition = 30;

      // Colors
      const primaryColor = [37, 99, 235]; // Blue
      const grayColor = [107, 114, 128]; // Gray

      // Report date
      const reportDate = new Date().toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Header Section
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 25, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Community Traffic Safety Statistics Report', margin, 22);

      // Report metadata
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(10);
      pdf.text(`Generated: ${reportDate}`, pageWidth - margin - 60, 22);

      yPosition = 35;

      // Executive Summary
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Executive Summary', margin, yPosition);
      yPosition += 15;

      // Summary text
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');

      // Calculate date range
      const sortedReports = filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const earliestDate =
        sortedReports.length > 0 ? new Date(sortedReports[0].createdAt) : new Date();
      const latestDate =
        sortedReports.length > 0
          ? new Date(sortedReports[sortedReports.length - 1].createdAt)
          : new Date();

      const dateRangeText = `${earliestDate.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })} to ${latestDate.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}`;

      // Get unique locations
      const uniqueLocations = [...new Set(filtered.map(report => report.location).filter(Boolean))];
      const locationText =
        uniqueLocations.length > 0
          ? uniqueLocations.slice(0, 3).join(', ') +
            (uniqueLocations.length > 3 ? ` and ${uniqueLocations.length - 3} other locations` : '')
          : 'Various locations';

      const summaryText = `This report covers traffic safety incidents reported from ${dateRangeText}. The data provides insights into incident patterns, trends, and safety concerns within the community.`;

      // Split text into lines that fit the page width
      const maxLineWidth = contentWidth - 10;
      const words = summaryText.split(' ');
      let currentLine = '';
      const lines = [];

      for (const word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const textWidth = pdf.getTextWidth(testLine);

        if (textWidth > maxLineWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) {
        lines.push(currentLine);
      }

      // Add the summary text lines
      for (const line of lines) {
        pdf.text(line, margin, yPosition);
        yPosition += 6;
      }

      yPosition += 10;

      // Key Statistics
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Total Reports: ${stats.total}`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Reports This Month: ${stats.thisMonth}`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Reported Injuries: ${stats.totalInjuries || 0}`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Injury Rate: ${stats.injuryRate || 0}%`, margin, yPosition);
      yPosition += 8;
      pdf.text(
        `Peak Hour: ${stats.peakHour || '—'} (${stats.peakHourCount || 0} incidents)`,
        margin,
        yPosition,
      );
      yPosition += 20;

      // Charts Section - Each chart on its own page
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Data Visualizations', margin, yPosition);
      yPosition += 15;

      // Chart 1: Line Chart - New Page
      pdf.addPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Incident Reports Over Time', margin, 30);
      yPosition = 50;

      if (lineChartRef.current) {
        try {
          // Find the canvas element within the chart container
          const canvasElement = lineChartRef.current.querySelector('canvas');
          if (canvasElement) {
            const canvas = await html2canvas(lineChartRef.current, {
              backgroundColor: '#ffffff',
              scale: 4,
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: lineChartRef.current.offsetWidth,
              height: lineChartRef.current.offsetHeight,
              scrollX: 0,
              scrollY: 0,
              windowWidth: window.innerWidth,
              windowHeight: window.innerHeight,
              pixelRatio: 3,
            });
            const imgData = canvas.toDataURL('image/png', 1.0);
            // Use larger dimensions for landscape chart
            const chartWidth = contentWidth - 10;
            const chartHeight = contentHeight - 40; // Leave space for title
            pdf.addImage(imgData, 'PNG', margin + 5, yPosition, chartWidth, chartHeight);
            console.log('✅ Line chart captured');
          } else {
            console.log('❌ Canvas element not found in line chart container');
            pdf.text('Line Chart: Canvas not found', margin, yPosition + 20);
            yPosition += 40;
          }
        } catch (err) {
          console.error('❌ Line chart error:', err);
          pdf.text('Line Chart: Error capturing', margin, yPosition + 20);
          yPosition += 40;
        }
      } else {
        console.log('❌ Line chart ref not available');
        pdf.text('Line Chart: Not available', margin, yPosition + 20);
        yPosition += 40;
      }

      // Chart 2: Pie Chart - New Page
      pdf.addPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Incident Type Distribution', margin, 30);
      yPosition = 50;

      if (pieChartRef.current) {
        try {
          // Find the canvas element within the chart container
          const canvasElement = pieChartRef.current.querySelector('canvas');
          if (canvasElement) {
            const canvas = await html2canvas(pieChartRef.current, {
              backgroundColor: '#ffffff',
              scale: 4,
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: pieChartRef.current.offsetWidth,
              height: pieChartRef.current.offsetHeight,
              scrollX: 0,
              scrollY: 0,
              windowWidth: window.innerWidth,
              windowHeight: window.innerHeight,
              pixelRatio: 3,
            });
            const imgData = canvas.toDataURL('image/png', 1.0);
            // Use larger dimensions for landscape chart
            const chartWidth = contentWidth - 10;
            const chartHeight = contentHeight - 40; // Leave space for title
            pdf.addImage(imgData, 'PNG', margin + 5, yPosition, chartWidth, chartHeight);
            console.log('✅ Pie chart captured');
          } else {
            console.log('❌ Canvas element not found in pie chart container');
            pdf.text('Pie Chart: Canvas not found', margin, yPosition + 20);
            yPosition += 40;
          }
        } catch (err) {
          console.error('❌ Pie chart error:', err);
          pdf.text('Pie Chart: Error capturing', margin, yPosition + 20);
          yPosition += 40;
        }
      } else {
        console.log('❌ Pie chart ref not available');
        pdf.text('Pie Chart: Not available', margin, yPosition + 20);
        yPosition += 40;
      }

      // Chart 3: Time of Day Chart - New Page
      pdf.addPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Incidents by Time of Day', margin, 30);
      yPosition = 50;

      if (timeOfDayChartRef.current) {
        try {
          // Find the canvas element within the chart container
          const canvasElement = timeOfDayChartRef.current.querySelector('canvas');
          if (canvasElement) {
            const canvas = await html2canvas(timeOfDayChartRef.current, {
              backgroundColor: '#ffffff',
              scale: 4,
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: timeOfDayChartRef.current.offsetWidth,
              height: timeOfDayChartRef.current.offsetHeight,
              scrollX: 0,
              scrollY: 0,
              windowWidth: window.innerWidth,
              windowHeight: window.innerHeight,
              pixelRatio: 3,
            });
            const imgData = canvas.toDataURL('image/png', 1.0);
            // Use larger dimensions for landscape chart
            const chartWidth = contentWidth - 10;
            const chartHeight = contentHeight - 40; // Leave space for title
            pdf.addImage(imgData, 'PNG', margin + 5, yPosition, chartWidth, chartHeight);
            console.log('✅ Time of day chart captured');
          } else {
            console.log('❌ Canvas element not found in time of day chart container');
            pdf.text('Time of Day Chart: Canvas not found', margin, yPosition + 20);
            yPosition += 40;
          }
        } catch (err) {
          console.error('❌ Time of day chart error:', err);
          pdf.text('Time of Day Chart: Error capturing', margin, yPosition + 20);
          yPosition += 40;
        }
      } else {
        console.log('❌ Time of day chart ref not available');
        pdf.text('Time of Day Chart: Not available', margin, yPosition + 20);
        yPosition += 40;
      }

      // Chart 4: Day of Week Chart - New Page
      pdf.addPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Incidents by Day of Week', margin, 30);
      yPosition = 50;

      if (peakHoursChartRef.current) {
        try {
          // Find the canvas element within the chart container
          const canvasElement = peakHoursChartRef.current.querySelector('canvas');
          if (canvasElement) {
            const canvas = await html2canvas(peakHoursChartRef.current, {
              backgroundColor: '#ffffff',
              scale: 4,
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: peakHoursChartRef.current.offsetWidth,
              height: peakHoursChartRef.current.offsetHeight,
              scrollX: 0,
              scrollY: 0,
              windowWidth: window.innerWidth,
              windowHeight: window.innerHeight,
              pixelRatio: 3,
            });
            const imgData = canvas.toDataURL('image/png', 1.0);
            // Use larger dimensions for landscape chart
            const chartWidth = contentWidth - 10;
            const chartHeight = contentHeight - 40; // Leave space for title
            pdf.addImage(imgData, 'PNG', margin + 5, yPosition, chartWidth, chartHeight);
            console.log('✅ Day of week chart captured');
          } else {
            console.log('❌ Canvas element not found in day of week chart container');
            pdf.text('Day of Week Chart: Canvas not found', margin, yPosition + 20);
            yPosition += 40;
          }
        } catch (err) {
          console.error('❌ Day of week chart error:', err);
          pdf.text('Day of Week Chart: Error capturing', margin, yPosition + 20);
          yPosition += 40;
        }
      } else {
        console.log('❌ Day of week chart ref not available');
        pdf.text('Day of Week Chart: Not available', margin, yPosition + 20);
        yPosition += 40;
      }

      // Add new page for insights
      pdf.addPage();
      yPosition = margin;

      // Key Insights
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Key Insights & Recommendations', margin, yPosition);
      yPosition += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const insights = [
        `• Peak incident time is ${stats.peakHour} with ${stats.peakHourCount} incidents`,
        `• Most common incident type is ${stats.mostCommonType}`,
        `• Injury rate is ${stats.injuryRate}% of all incidents`,
        `• Total of ${stats.totalInjuries} reported injuries across all incidents`,
        '• Consider targeted safety measures during peak hours',
        '• Focus prevention efforts on most common incident types',
        '• Monitor injury trends for safety improvement opportunities',
      ];

      insights.forEach(insight => {
        pdf.text(insight, margin, yPosition);
        yPosition += 8;
      });

      // Footer
      pdf.setFillColor(248, 250, 252);
      pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('© 2026 SWARM Incident Reporter', margin, pageHeight - 8);
      pdf.text(
        `Report generated from ${filtered.length} incident reports`,
        pageWidth - margin - 80,
        pageHeight - 8,
      );

      // Save the PDF
      const fileName = `swarm-safety-report-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      console.log('✅ PDF saved successfully');
    } catch (error) {
      console.error('❌ Error exporting to PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      setError(`Error generating PDF report: ${error.message}. Please try again.`);
      alert(`Error generating PDF report: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToXLSX = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { createWorkbook, addSheetFromObjects, downloadWorkbook } = await import(
        '../utils/excelExport'
      );
      const workbook = createWorkbook();
      addSheetFromObjects(
        workbook,
        'Incident Reports',
        filtered.map(r => ({
          Reference: `SWARM${r.id}`,
          Type: r.incidentType,
          Description: r.description,
          Location: r.location,
          Date: r.estimatedIncidentTime,
          Injuries: r.injuries,
          'Emergency Services': r.emergencyServicesAttended,
          Status: r.isApproved ? 'Approved' : 'Pending',
        })),
      );
      await downloadWorkbook(workbook, 'swarm-incident-reports.xlsx');
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
      setError('Error generating XLSX report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique incident types for filter dropdown
  const incidentTypes = useMemo(() => {
    try {
      if (!reports || !Array.isArray(reports)) return [];
      const types = [...new Set(reports.map(r => r?.incidentType).filter(Boolean))];
      return types.sort();
    } catch (err) {
      console.error('Error getting incident types:', err);
      return [];
    }
  }, [reports]);

  // Show error state
  if (error) {
    console.log('🔍 Component in error state:', error);
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center p-8">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Statistics</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => setError(null)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    console.log('🔍 Component in loading state');
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading statistics...</div>
        </div>
      </div>
    );
  }

  console.log('🔍 Component rendering charts - state:', {
    isLoading,
    error,
    reportsLength: reports?.length || 0,
    filteredLength: filtered.length,
    lineChartRef: lineChartRef.current,
    pieChartRef: pieChartRef.current,
  });

  return (
    <div className="flex flex-col h-full">
      {/* Mobile: Filters at top */}
      <div className="lg:hidden">
        {/* Mobile Filter Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-between w-full p-3 bg-white rounded-lg shadow-sm hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-primary-200"
          >
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className="flex items-center space-x-2 min-w-0">
                <span className="text-sm font-medium text-gray-700">Report Filters</span>
                {/* Active filter indicators */}
                {(typeFilter !== 'all' ||
                  (user && user.role === 'admin' && includePending) ||
                  dateRange.from ||
                  dateRange.to) && (
                  <div className="flex items-center space-x-1 flex-wrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Active
                    </span>
                    {typeFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Type
                      </span>
                    )}
                    {user && user.role === 'admin' && includePending && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Pending
                      </span>
                    )}
                    {(dateRange.from || dateRange.to) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Date
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                ({filtered.length} reports)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">{sidebarOpen ? 'Hide' : 'Show'}</span>
              <div
                className={`transform transition-transform duration-200 ${sidebarOpen ? 'rotate-180' : ''}`}
              >
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </button>
        </div>

        {/* Mobile Filter Content */}
        {sidebarOpen && (
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="bg-white rounded-lg p-4 space-y-4">
              {/* Mobile tip */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600 text-sm">💡</span>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Mobile Tip:</p>
                    <p>
                      For better chart visibility, rotate your device to landscape mode or use the
                      export buttons below.
                    </p>
                  </div>
                </div>
              </div>

              {/* Filter explanation */}
              <div className="text-sm text-gray-600 mb-4">
                <p className="font-medium mb-1">Filter Options:</p>
                <p>
                  Use these filters to analyze specific types of incidents, date ranges, and report
                  statuses. Changes apply instantly to all charts and statistics below.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {user && user.role === 'admin' && (
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includePending}
                        onChange={e => setIncludePending(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Include Pending Reports
                      </span>
                    </label>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Incident Type
                  </label>
                  <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="all">All Types</option>
                    {incidentTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2">
                <button
                  onClick={exportToPDF}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  onClick={exportToXLSX}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Exporting...' : 'Export XLSX'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Filters at top */}
      <div className="hidden lg:block">
        {/* Desktop Filter Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-between w-full p-3 bg-white rounded-lg shadow-sm hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-primary-200"
          >
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className="flex items-center space-x-2 min-w-0">
                <span className="text-sm font-medium text-gray-700">Report Filters</span>
                {/* Active filter indicators */}
                {(typeFilter !== 'all' ||
                  (user && user.role === 'admin' && includePending) ||
                  dateRange.from ||
                  dateRange.to) && (
                  <div className="flex items-center space-x-1 flex-wrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Active
                    </span>
                    {typeFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Type
                      </span>
                    )}
                    {user && user.role === 'admin' && includePending && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Pending
                      </span>
                    )}
                    {(dateRange.from || dateRange.to) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Date
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                ({filtered.length} reports)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">{sidebarOpen ? 'Hide' : 'Show'}</span>
              <div
                className={`transform transition-transform duration-200 ${sidebarOpen ? 'rotate-180' : ''}`}
              >
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </button>
        </div>

        {/* Desktop Filter Content */}
        {sidebarOpen && (
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="bg-white rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {user && user.role === 'admin' && (
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includePending}
                        onChange={e => setIncludePending(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Include Pending Reports
                      </span>
                    </label>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Incident Type
                  </label>
                  <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="all">All Types</option>
                    {incidentTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={exportToPDF}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  onClick={exportToXLSX}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Exporting...' : 'Export XLSX'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main content - now takes full width */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 lg:p-6">
          <h2 className="text-xl lg:text-2xl font-bold text-primary-800 mb-4 lg:mb-6">
            Graphs & Statistics
          </h2>

          {/* Enhanced Stats Cards - Desktop only (shown at top) */}
          <div className="hidden lg:flex mb-8 gap-3">
            <div className="bg-primary-50 rounded-lg p-3 text-center flex-1">
              <div className="text-xl font-bold text-primary-700">{stats.total}</div>
              <div className="text-xs text-gray-700 mt-1">
                Total incident reports in filtered data
              </div>
            </div>
            <div className="bg-secondary-50 rounded-lg p-3 text-center flex-1">
              <div className="text-xl font-bold text-secondary-700">{stats.thisMonth}</div>
              <div className="text-xs text-gray-700 mt-1">Reports from current month</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center flex-1">
              <div className="text-xl font-bold text-red-700">{stats.totalInjuries || 0}</div>
              <div className="text-xs text-gray-700 mt-1">Total reported injuries</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center flex-1">
              <div className="text-xl font-bold text-orange-700">{stats.injuryRate || 0}%</div>
              <div className="text-xs text-gray-700 mt-1">% of incidents with injuries</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center flex-1">
              <div className="text-xl font-bold text-purple-700">{stats.peakHour || '—'}</div>
              <div className="text-xs text-gray-700 mt-1">Hour with most incidents</div>
            </div>
          </div>

          {/* Map Section - Moved to Top */}
          <div
            className="bg-white rounded-xl shadow-soft p-4 lg:p-6 border border-gray-100 mb-8"
            ref={mapRef}
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
              <h3 className="text-lg font-semibold">Incident Location Analysis</h3>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  onClick={() => setMapView('clustering')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mapView === 'clustering'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Clustering
                </button>
                <button
                  onClick={() => setMapView('heatmap')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mapView === 'heatmap'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Heatmap
                </button>
              </div>
            </div>

            {mapView === 'clustering' ? (
              <IncidentClusterMap
                reports={filtered}
                typeFilter={typeFilter}
                dateRange={dateRange}
                includePending={includePending}
              />
            ) : (
              <IncidentHeatmap
                reports={filtered}
                typeFilter={typeFilter}
                dateRange={dateRange}
                includePending={includePending}
              />
            )}
          </div>

          {/* Original Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={lineChartRef}
              data-chart="line"
            >
              <h3 className="text-lg font-semibold mb-4">Incident Reports Over Time</h3>
              <div style={{ height: '400px', width: '100%' }}>
                <Line data={lineData} options={chartOptions} />
              </div>
            </div>
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={pieChartRef}
              data-chart="pie"
            >
              <h3 className="text-lg font-semibold mb-4">Incident Type Distribution</h3>
              <div style={{ height: '400px', width: '100%' }}>
                <Pie
                  data={pieData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        ...chartOptions.plugins.legend,
                        position: 'right',
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Time of Day and Day of Week Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={timeOfDayChartRef}
              data-chart="timeOfDay"
            >
              <h3 className="text-lg font-semibold mb-4">Incidents by Time of Day</h3>
              <div className="text-sm text-gray-600 mb-4">
                Shows incident frequency throughout the day (24-hour format)
              </div>
              <div style={{ height: '400px', width: '100%' }}>
                <Line
                  data={timeOfDayData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        ...chartOptions.plugins.tooltip,
                        callbacks: {
                          title(context) {
                            return `Hour: ${context[0].label}`;
                          },
                          label(context) {
                            return `Incidents: ${context.parsed.y}`;
                          },
                        },
                      },
                    },
                    scales: {
                      ...chartOptions.scales,
                      x: {
                        ...chartOptions.scales.x,
                        title: {
                          display: true,
                          text: 'Hour of Day',
                          font: {
                            size: 12,
                            family: 'Arial, sans-serif',
                          },
                          color: '#374151',
                        },
                      },
                      y: {
                        ...chartOptions.scales.y,
                        title: {
                          display: true,
                          text: 'Number of Incidents',
                          font: {
                            size: 12,
                            family: 'Arial, sans-serif',
                          },
                          color: '#374151',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div
              className="bg-white rounded-xl shadow-soft p-6 border border-gray-100"
              ref={peakHoursChartRef}
              data-chart="dayOfWeek"
            >
              <h3 className="text-lg font-semibold mb-4">Incidents by Day of Week</h3>
              <div className="text-sm text-gray-600 mb-4">
                Shows which days of the week have the most incidents
              </div>
              <div style={{ height: '400px', width: '100%' }}>
                <Bar
                  data={peakHoursData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        ...chartOptions.plugins.tooltip,
                        callbacks: {
                          title(context) {
                            const dayIndex = context[0].dataIndex;
                            const dayNames = [
                              'Sunday',
                              'Monday',
                              'Tuesday',
                              'Wednesday',
                              'Thursday',
                              'Friday',
                              'Saturday',
                            ];
                            return dayNames[dayIndex];
                          },
                          label(context) {
                            return `Incidents: ${context.parsed.y}`;
                          },
                        },
                      },
                    },
                    scales: {
                      ...chartOptions.scales,
                      x: {
                        ...chartOptions.scales.x,
                        title: {
                          display: true,
                          text: 'Day of Week',
                          font: {
                            size: 12,
                            family: 'Arial, sans-serif',
                          },
                          color: '#374151',
                        },
                      },
                      y: {
                        ...chartOptions.scales.y,
                        title: {
                          display: true,
                          text: 'Number of Incidents',
                          font: {
                            size: 12,
                            family: 'Arial, sans-serif',
                          },
                          color: '#374151',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Most Common Type Card */}
          <div className="bg-success-50 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-success-700">{stats.mostCommonType || '—'}</div>
            <div className="text-gray-700 mt-2">Most Common Incident Type</div>
          </div>

          {/* Mobile Stats Cards - Shown at bottom on mobile */}
          <div className="lg:hidden mt-8 grid grid-cols-2 gap-4">
            <div className="bg-primary-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-primary-700">{stats.total}</div>
              <div className="text-sm text-gray-700 mt-1">
                Total incident reports in filtered data
              </div>
            </div>
            <div className="bg-secondary-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-secondary-700">{stats.thisMonth}</div>
              <div className="text-sm text-gray-700 mt-1">Reports from current month</div>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-red-700">{stats.totalInjuries || 0}</div>
              <div className="text-sm text-gray-700 mt-1">Total reported injuries</div>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-orange-700">{stats.injuryRate || 0}%</div>
              <div className="text-sm text-gray-700 mt-1">% of incidents with injuries</div>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center col-span-2">
              <div className="text-2xl font-bold text-purple-700">{stats.peakHour || '—'}</div>
              <div className="text-sm text-gray-700 mt-1">Hour with most incidents</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
